import { Avatar, Box, Link, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { applicantService } from '../../../../services/applicant.service';
import type { ApplicantMessage, CommunicationMedium } from '../../../../services/types/applicant.types';
import {
  formatDateSeparator,
  formatTime,
  getFilenameFromUrl,
  getInitials,
  groupMessagesByDate,
  isLastInMessageBurst,
  isSameMessageBurst,
  sortMessagesByDateTime,
} from '../../../../utils/messageUtils';
import Alert from '../../../common/Alert';
import Loading from '../../../common/Loading';
import EyepaxLogo from '../../../../assets/icons/Eyepax Symbol 1.5x.svg';

interface MessagesProps {
  applicantName?: string;
  applicantMobile?: string;
}

const MEDIUM_ICON_MAP: Record<CommunicationMedium, React.ReactNode> = {
  WHATSAPP: <WhatsAppIcon sx={{ fontSize: 14, color: '#25D366' }} />,
  EMAIL: <EmailOutlinedIcon sx={{ fontSize: 14, color: '#702DFF' }} />,
  ZALO: <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: '#0068FF' }} />,
};

const AttachmentRenderer: React.FC<{ message: ApplicantMessage }> = ({ message }) => {
  const { message_type, attachment_url, message_text } = message;

  if (message_type === 'TEXT' || !attachment_url) {
    return null;
  }

  switch (message_type) {
    case 'IMAGE':
      return (
        <Box sx={{ mt: message_text ? 1 : 0 }}>
          <a href={attachment_url} target="_blank" rel="noopener noreferrer">
            <Box
              component="img"
              src={attachment_url}
              alt="Attachment"
              sx={{
                maxWidth: 300,
                borderRadius: '8px',
                display: 'block',
              }}
            />
          </a>
        </Box>
      );
    case 'DOCUMENT':
      return (
        <Box sx={{ mt: message_text ? 1 : 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 19, color: '#667085' }} />
          <Link
            href={attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '13.5px', color: '#702DFF', textDecoration: 'underline' }}
          >
            {getFilenameFromUrl(attachment_url)}
          </Link>
        </Box>
      );
    case 'AUDIO':
      return (
        <Box sx={{ mt: message_text ? 1 : 0 }}>
          <audio controls style={{ maxWidth: 300 }}>
            <source src={attachment_url} />
          </audio>
        </Box>
      );
    case 'VIDEO':
      return (
        <Box sx={{ mt: message_text ? 1 : 0 }}>
          <video controls style={{ maxWidth: 300, borderRadius: 8 }}>
            <source src={attachment_url} />
          </video>
        </Box>
      );
    default:
      return null;
  }
};

const MediumBadge: React.FC<{ medium: CommunicationMedium }> = ({ medium }) => (
  <Box
    data-testid={`medium-badge-${medium}`}
    sx={{
      position: 'absolute',
      bottom: -2,
      right: -2,
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      border: '1px solid #EAECF0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {MEDIUM_ICON_MAP[medium]}
  </Box>
);

const MessageBubble: React.FC<{
  message: ApplicantMessage;
  applicantName: string;
  senderChanged: boolean;
  showTimestamp: boolean;
  sameBurst: boolean;
}> = ({ message, applicantName, senderChanged, showTimestamp, sameBurst }) => {
  const isCompany = message.sender_type !== 0;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isCompany ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        mb: sameBurst ? 0.5 : 1,
      }}
    >
      {!isCompany && senderChanged && (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: '#702DFF',
              color: '#FFFFFF',
              fontSize: '15px',
              fontWeight: 600,
            }}
            aria-label={applicantName}
          >
            {getInitials(applicantName)}
          </Avatar>
          <MediumBadge medium={message.communication_medium} />
        </Box>
      )}
      {!isCompany && !senderChanged && <Box sx={{ width: 38, flexShrink: 0 }} />}

      <Box
        sx={{
          maxWidth: '55%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCompany ? 'flex-end' : 'flex-start',
        }}
      >
        <Box
          sx={{
            backgroundColor: isCompany ? '#F1F3FF' : '#F9FAFB',
            borderRadius: '8px',
            padding: '12px 16px',
          }}
        >
          {message.message_text && (
            <Typography
              sx={{
                fontSize: '13.5px',
                lineHeight: '1.5',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                color: '#344054',
              }}
            >
              {message.message_text}
            </Typography>
          )}
          <AttachmentRenderer message={message} />
        </Box>

        {showTimestamp && (
          <Typography sx={{ fontSize: '11px', color: '#667085', mt: '2px' }}>
            {formatTime(message.sent_time)}
          </Typography>
        )}
      </Box>

      {isCompany && senderChanged && (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: '#F1F3FF',
            }}
            aria-label="Company"
          >
            <Box
              component="img"
              src={EyepaxLogo}
              alt="Company logo"
              sx={{ width: 28, height: 28 }}
            />
          </Avatar>
          <MediumBadge medium={message.communication_medium} />
        </Box>
      )}
      {isCompany && !senderChanged && <Box sx={{ width: 38, flexShrink: 0 }} />}
    </Box>
  );
};

const Messages: React.FC<MessagesProps> = ({
  applicantName = 'Applicant',
}) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState<ApplicantMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchMessages = async () => {
      try {
        setIsLoading(true);
        const response = await applicantService.getMessages(id);
        setMessages(response.data || []);
        setError(null);
      } catch {
        setError('Failed to load messages');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [id]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert message={error} severity="error" />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box className="view-section">
        <Typography sx={{ color: '#667085', fontSize: '13.5px' }}>
          No messages yet
        </Typography>
      </Box>
    );
  }

  const sorted = sortMessagesByDateTime(messages);
  const grouped = groupMessagesByDate(sorted);
  const sortedDates = Object.keys(grouped).sort();

  return (
    <Box
      className="view-section"
      data-testid="messages-container"
      sx={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', scrollBehavior: 'smooth' }}
    >
      {sortedDates.map((dateKey) => (
        <React.Fragment key={dateKey}>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#F9FAFB',
                borderRadius: '12px',
                px: 1.5,
                py: 0.5,
              }}
            >
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                {formatDateSeparator(dateKey)}
              </Typography>
            </Box>
          </Box>

          {grouped[dateKey].map((message, index) => {
            const arr = grouped[dateKey];
            const prev = index > 0 ? arr[index - 1] : null;
            const next = index < arr.length - 1 ? arr[index + 1] : null;
            const senderChanged = !prev || prev.sender_type !== message.sender_type;
            const sameBurst = isSameMessageBurst(prev, message);
            const showTimestamp = isLastInMessageBurst(message, next);

            return (
              <MessageBubble
                key={message.id}
                message={message}
                applicantName={applicantName}
                senderChanged={senderChanged}
                showTimestamp={showTimestamp}
                sameBurst={sameBurst}
              />
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default Messages;
